import type { DeckPage, Signal } from '../types/presentation'

/** Client name used across the presentation. */
export const client = 'Abbott'

export const deckMeta = {
  concept: `See beyond the fluid · Snakker × ${client}`,
  running: `Snakker × ${client} · From creative idea to commercial impact`,
  footer: 'Concept & creative direction © Snakker™ 2026 · Confidential & proprietary',
}

export const deckPages: DeckPage[] = [
  { id: 'opening', label: 'See beyond the fluid' },
  { id: 'journey', label: 'Attract. Convince. Convert.' },
  { id: 'channels', label: 'One experience. More channels to share.' },
  { id: 'useCases', label: 'One format. Many use cases.' },
  { id: 'signals', label: 'Every interaction creates a signal' },
  { id: 'compound', label: 'Make that activity compound' },
  { id: 'station', label: 'Snakker Station' },
  { id: 'campaign', label: 'The always-on campaign' },
]

export const hero = {
  title: 'See beyond the fluid',
  lead: 'A short, scientific Snak that earns the treatment conversation — one phenotype at a time.',
}

/* ---------- Journey ---------- */

export type StageId = 'attract' | 'convince' | 'convert'

export interface JourneyStage {
  id: StageId
  name: string
  channels: string
  goal: string
}

export const journey: JourneyStage[] = [
  {
    id: 'attract',
    name: 'Attract',
    channels: 'Hooks, teasers, email, LinkedIn, rep follow-ups and congress.',
    goal: 'Bring more relevant specialists into the experience.',
  },
  {
    id: 'convince',
    name: 'Convince',
    channels: 'See beyond the fluid — the short, scientific, lean-back Snak.',
    goal: 'Make the science understandable, memorable and clinically relevant.',
  },
  {
    id: 'convert',
    name: 'Convert',
    channels: 'Phenotyping walkthroughs, case reviews, pilots and medical follow-up.',
    goal: `Contextual next steps connect specialists directly to ${client}’s medical and brand teams.`,
  },
]

export const attractChannels = ['Email', 'LinkedIn', 'Rep follow-up', 'Congress']

export const convertActions = [
  'Phenotyping walkthrough',
  'Case review',
  'Join the pilot',
  'Medical follow-up',
]

export const journeyClosing = ['Simplify the science.', 'Respect the audience.', 'Drive meaningful actions.']

/* ---------- Channels ---------- */

export type ChannelId =
  | 'embed'
  | 'messaging'
  | 'detailer'
  | 'qr'
  | 'post'
  | 'email'
  | 'advertise'
  | 'congress'

export interface ShareChannel {
  id: ChannelId
  name: string
  detail: string
}

export const shareChannels: ShareChannel[] = [
  { id: 'embed', name: 'Embed', detail: 'HCP portals and websites' },
  { id: 'messaging', name: 'SMS & WhatsApp', detail: 'Direct links, conversation-ready' },
  { id: 'detailer', name: 'Rep detailer', detail: 'Remote and face-to-face calls' },
  { id: 'qr', name: 'QR code', detail: 'Print, posters and events' },
  { id: 'post', name: 'Post', detail: 'LinkedIn, organic and paid' },
  { id: 'email', name: 'Email', detail: 'CRM and newsletters' },
  { id: 'advertise', name: 'Advertise', detail: 'Banners and medical media' },
  { id: 'congress', name: 'Congress', detail: 'Booths, symposia and iPads' },
]

export const snakkerSupport = ['Adaptation', 'Production', 'Campaign deployment', 'Measurement', 'Optimization']

/* ---------- Use cases ---------- */

export type UseCaseIcon = 'research' | 'door' | 'attention' | 'study' | 'email' | 'congress'

export interface UseCase {
  id: UseCaseIcon
  name: string
  barrier: string
  example: string
}

export const useCases: UseCase[] = [
  {
    id: 'research',
    name: 'Market research pulse',
    barrier: 'We don’t know what specialists really think.',
    example: 'Three questions on how DME is phenotyped today.',
  },
  {
    id: 'door',
    name: 'Door opener',
    barrier: 'Reps can’t get the first meeting.',
    example: 'A 60-second science teaser sent before the visit.',
  },
  {
    id: 'attention',
    name: 'Attention grabber',
    barrier: 'Banners and posts get scrolled past.',
    example: 'One provocative OCT question that pulls people in.',
  },
  {
    id: 'study',
    name: 'Quick study update',
    barrier: 'New data is published, but nobody reads the paper.',
    example: 'The four-cluster analysis in five screens.',
  },
  {
    id: 'email',
    name: 'Email outreach',
    barrier: 'Newsletters are opened, then closed.',
    example: 'A prior-and-update probe that invites a reply.',
  },
  {
    id: 'congress',
    name: 'Congress activation',
    barrier: 'Booth traffic doesn’t turn into follow-up.',
    example: 'A booth iPad Snak that books a walkthrough.',
  },
]

/* ---------- Signals ---------- */

export const signalLoop = ['Signals', 'Next campaign', 'Better activation']

export type MeasureId = 'archetype' | 'prior' | 'progress' | 'nextStep' | 'sources'

export interface Measure {
  id: MeasureId
  question: string
}

export const measures: Measure[] = [
  { id: 'archetype', question: 'Which archetype do specialists choose — Foundational, Developing or Advanced?' },
  { id: 'prior', question: 'How far do their views move once the evidence is shown?' },
  { id: 'progress', question: 'How far do they progress through each instalment?' },
  { id: 'nextStep', question: 'Which scientific next step do they request?' },
  { id: 'sources', question: 'Which hooks and channels bring engaged specialists back?' },
]

/** "What we learn" overlay. */
export const signals: Signal[] = [
  { id: 'archetype', label: 'Archetype selected', detail: 'Where specialists place themselves on the adoption curve.' },
  { id: 'quiz', label: 'Quiz response', detail: 'How they rate the unmet need — before any data is shown.' },
  { id: 'prior', label: 'Stated priors', detail: 'What they expect about band disruption and reader agreement.' },
  { id: 'update', label: 'Prior shift', detail: 'How far a view moves once the evidence is in front of them.' },
  { id: 'clusters', label: 'Clusters explored', detail: 'Which phenotypes they open and spend time with.' },
  { id: 'completion', label: 'Instalment completion', detail: 'How far through each argument they stay.' },
  { id: 'nextStep', label: 'Next step requested', detail: 'Walkthrough, case review or pilot — who acts, and on what.' },
]

export const signalsClosing = 'The Snak shows not only what specialists read, but how their thinking moved.'

/* ---------- Compound (Control Tower) ---------- */

export const controlTowerScreenshot =
  'https://cdn.magicpatterns.com/uploads/jYPAzbYJKofKppr5diGJM3/Screenshot_2026-10-22_at_11.02.09.png'

export const controlTowerSource = { width: 2168, height: 1636 }

export interface Crop {
  x: number
  y: number
  width: number
  height: number
}

export type CompoundIcon = 'see' | 'learn' | 'reuse' | 'spot' | 'buy'

export const compoundFeatures: {
  key: CompoundIcon
  title: string
  detail: string
  crop: Crop
  alt: string
}[] = [
  {
    key: 'see',
    title: 'See what works',
    detail: 'Across brands, countries and channels.',
    crop: { x: 78, y: 632, width: 945, height: 520 },
    alt: 'Snakker Brand Intelligence overview: markets assessed, products tracked and gap severity by market',
  },
  {
    key: 'learn',
    title: 'Learn faster',
    detail: 'Apply success from one market to another.',
    crop: { x: 1128, y: 640, width: 950, height: 505 },
    alt: 'Snakker Brand Intelligence product view with a world map of gap severity and root-cause breakdown',
  },
]

export const compoundBenefits: { key: CompoundIcon; title: string; detail: string }[] = [
  { key: 'reuse', title: 'Reuse more', detail: 'Adapt instead of rebuild.' },
  { key: 'spot', title: 'Spot opportunities', detail: 'Identify where proven ideas can travel.' },
  { key: 'buy', title: 'Build buying power', detail: 'More volume creates lower unit cost.' },
]

/* ---------- Station ---------- */

export const stationScreenshot =
  'https://cdn.magicpatterns.com/uploads/9PgVysqJh8jC3Dw6oZmj8M/Screenshot_2026-10-22_at_11.03.14.png'

export const stationSource = { width: 2520, height: 1479 }
export const shelfThumb = { width: 313, height: 321 }

export const stationFilters = [
  'All',
  'Market research',
  'Rep support',
  'Banners / hooks',
  'Study update',
  'Congress',
  'Follow-up',
] as const

export type StationFilter = (typeof stationFilters)[number]

export interface ShelfSnak {
  id: string
  title: string
  category: Exclude<StationFilter, 'All'>
  x: number
  y: number
}

export const shelfSnaks: ShelfSnak[] = [
  { id: 'itch', title: 'Just an itch? — evidence challenge', category: 'Congress', x: 1075, y: 284 },
  { id: 'study', title: '87% — four-year study update', category: 'Study update', x: 1417, y: 284 },
  { id: 'pulse', title: 'Eye Care Pulse — peer poll', category: 'Market research', x: 1759, y: 284 },
  { id: 'booth', title: 'The analysis — booth iPad', category: 'Congress', x: 2101, y: 284 },
  { id: 'oct', title: '5 OCT variables — follow-up', category: 'Follow-up', x: 1075, y: 744 },
  { id: 'guide', title: 'Patient application guide', category: 'Rep support', x: 1417, y: 744 },
  { id: 'banner', title: 'Have you seen it yet? — banner', category: 'Banners / hooks', x: 1759, y: 744 },
]

export const featuredSnak = {
  category: 'Scientific engagement',
  title: 'See beyond the fluid — DME phenotyping',
  usedIn: 'Three instalments · Foundational, Developing, Advanced',
  solves: 'Earn credibility with a small, highly scientific audience before any product claim',
  channels: ['Email', 'Rep iPad', 'Congress'],
}

export const stationPillars = [
  { label: 'Control', text: 'Control Tower provides central visibility.' },
  { label: 'Empower', text: 'Station gives local markets access to existing Snaks and inspiration.' },
  { label: 'Optimize', text: 'Performance intelligence, pulse insights and recommendations help everyone improve.' },
]

/* ---------- Campaign ---------- */

export const campaignStages = [
  { name: 'Hook', detail: 'A short message invites specialists in.' },
  { name: 'Snak', detail: 'An OCT case, a question and instant feedback.' },
  { name: 'Insight', detail: 'Every answer feeds the campaign report.' },
]

export const reuseBlocks = ['Thinking', 'Arguments', 'Evidence', 'Formats']

export const nextSteps = [
  { verb: 'Start', text: 'with the See beyond the fluid concept.' },
  { verb: 'Launch', text: 'Snak 1 in one market.' },
  { verb: 'Measure', text: 'the response.' },
  { verb: 'Expand', text: 'what works.' },
]
